import { APP_LINKS } from "../constants/appStoreLinks";

const BADGE_HEIGHT = {
  default: "h-10 md:h-11",
  large: "h-14 sm:h-16 md:h-[72px] lg:h-20",
};

function AppStoreBadge({ href, theme = "dark", size = "default" }) {
  const isDark = theme === "dark";
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Download on the App Store"
      className={`inline-block ${BADGE_HEIGHT[size] ?? BADGE_HEIGHT.default} transition-opacity hover:opacity-90`}
    >
      <svg
        viewBox="0 0 120 40"
        className="h-full w-auto"
        role="img"
        aria-hidden="true"
      >
        <rect
          width="120"
          height="40"
          rx="6"
          fill={isDark ? "#000" : "#fff"}
          stroke={isDark ? "transparent" : "#A6A6A6"}
        />
        <path
          d="M24.77 20.3c-.03-3.2 2.62-4.72 2.74-4.78-1.49-2.18-3.81-2.48-4.64-2.51-1.98-.2-3.87 1.17-4.87 1.17-.99 0-2.52-1.14-4.15-1.11-2.13.03-4.1 1.24-5.2 3.15-2.22 3.85-.57 9.54 1.6 12.66 1.06 1.53 2.32 3.25 3.98 3.19 1.6-.06 2.2-1.03 4.13-1.03 1.93 0 2.47 1.03 4.15.99 1.72-.03 2.8-1.56 3.85-3.1 1.21-1.77 1.71-3.48 1.74-3.57-.04-.02-3.34-1.28-3.37-5.06zM21.3 11.87c.88-1.07 1.47-2.56 1.31-4.04-1.27.05-2.8.84-3.71 1.9-.82.95-1.53 2.47-1.34 3.93 1.42.11 2.87-.72 3.74-1.79z"
          fill={isDark ? "#fff" : "#000"}
        />
        <text
          x="38"
          y="14"
          fill={isDark ? "#fff" : "#000"}
          fontSize="5.5"
          fontFamily="system-ui, sans-serif"
        >
          Download on the
        </text>
        <text
          x="38"
          y="27"
          fill={isDark ? "#fff" : "#000"}
          fontSize="11"
          fontWeight="600"
          fontFamily="system-ui, sans-serif"
        >
          App Store
        </text>
      </svg>
    </a>
  );
}

function GooglePlayBadge({ href, theme = "dark", size = "default" }) {
  const isDark = theme === "dark";
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Get it on Google Play"
      className={`inline-block ${BADGE_HEIGHT[size] ?? BADGE_HEIGHT.default} transition-opacity hover:opacity-90`}
    >
      <svg
        viewBox="0 0 135 40"
        className="h-full w-auto"
        role="img"
        aria-hidden="true"
      >
        <rect
          width="135"
          height="40"
          rx="6"
          fill={isDark ? "#000" : "#fff"}
          stroke={isDark ? "transparent" : "#A6A6A6"}
        />
        <path
          d="M8.5 8.2l7.8 11.8-7.8 11.8c-.4-.2-.7-.6-.7-1.1V9.3c0-.5.3-.9.7-1.1z"
          fill="#00D0FF"
        />
        <path
          d="M16.3 20l10.5 6.1-8.9 5.2L8.5 20h7.8z"
          fill="#FFE000"
        />
        <path
          d="M16.3 20L8.5 8.2l10.5 5.7L26.8 20H16.3z"
          fill="#FF3A44"
        />
        <path
          d="M16.3 20l10.5 6.1 8.9 5.2L26.8 20H16.3z"
          fill="#00F076"
        />
        <text
          x="38"
          y="14"
          fill={isDark ? "#fff" : "#000"}
          fontSize="5.5"
          fontFamily="system-ui, sans-serif"
        >
          GET IT ON
        </text>
        <text
          x="38"
          y="27"
          fill={isDark ? "#fff" : "#000"}
          fontSize="11"
          fontWeight="600"
          fontFamily="system-ui, sans-serif"
        >
          Google Play
        </text>
      </svg>
    </a>
  );
}

/**
 * @param {"customer" | "driver"} appType
 * @param {"dark" | "light"} theme — dark badges for light backgrounds, light-style for footer
 * @param {string} className
 */
export default function AppDownloadBadges({
  appType = "customer",
  theme = "dark",
  size = "default",
  className = "",
}) {
  const links = APP_LINKS[appType] ?? APP_LINKS.customer;

  return (
    <div className={`flex flex-wrap items-center justify-center gap-4 md:gap-6 ${className}`}>
      <AppStoreBadge href={links.ios} theme={theme} size={size} />
      <GooglePlayBadge href={links.android} theme={theme} size={size} />
    </div>
  );
}

export function AppDownloadSection({ className = "", audience = "both" }) {
  const customerOnly = audience === "customer";

  if (customerOnly) {
    return (
      <section
        id="download-apps"
        className={`w-full py-14 md:py-20 lg:py-24 bg-gradient-to-b from-[#F6FCFF] to-white ${className}`}
      >
        <div className="w-full px-4 md:px-8 lg:px-14 text-center">
          <h2 className="text-[32px] sm:text-[40px] md:text-[48px] lg:text-[56px] font-extrabold text-[#023B64] leading-tight">
            Download the Tydii App
          </h2>
          <p className="text-gray-600 mt-4 md:mt-6 text-lg md:text-xl lg:text-2xl max-w-3xl mx-auto leading-relaxed">
            Book laundry pickup and delivery from your phone — schedule, track,
            and pay in one place.
          </p>
          <p className="text-[#50555C] mt-3 md:mt-4 text-base md:text-lg max-w-2xl mx-auto">
            Schedule pickups, track orders, and pay securely from your phone.
          </p>
          <div className="mt-10 md:mt-14 lg:mt-16">
            <AppDownloadBadges appType="customer" size="large" />
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      id="download-apps"
      className={`mx-4 md:mx-6 lg:mx-14 py-10 md:py-16 ${className}`}
    >
      <div className="text-center mb-10">
        <h2 className="text-[28px] md:text-[34px] font-extrabold text-[#023B64]">
          Download the Tydii App
        </h2>
        <p className="text-gray-600 mt-2 text-base md:text-lg max-w-2xl mx-auto">
          Book laundry pickup and delivery, or drive with Tydii — get the app
          for your role.
        </p>
      </div>

      <div className="grid gap-8 mx-auto max-w-4xl grid-cols-1 md:grid-cols-2">
        <div className="bg-white rounded-2xl border border-gray-100 p-8 shadow-md flex flex-col items-center text-center">
          <h3 className="text-xl font-bold text-[#023B64] mb-2">For Customers</h3>
          <p className="text-gray-600 text-sm mb-6">
            Schedule pickups, track orders, and pay securely from your phone.
          </p>
          <AppDownloadBadges appType="customer" />
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 p-8 shadow-md flex flex-col items-center text-center">
          <h3 className="text-xl font-bold text-[#023B64] mb-2">For Drivers</h3>
          <p className="text-gray-600 text-sm mb-6">
            Accept routes, navigate pickups and deliveries, and manage earnings.
          </p>
          <AppDownloadBadges appType="driver" />
        </div>
      </div>
    </section>
  );
}
